import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpenIcon,
  AwardIcon,
  ClockIcon,
  CheckCircle2Icon,
  UsersIcon,
  ExternalLinkIcon,
  ArrowLeftIcon,
  SearchIcon
} from './PartnerIcons';

interface SharedArticle {
  id: string;
  code: string;
  title: string;
  journal: string;
  indexation: string;
  quartile: string;
  stage: string;
  stageColor: string;
  coauthors: string[];
  lastUpdate: string;
  doi?: string;
}

const PRESET_ARTICLES: SharedArticle[] = [
  {
    id: 'a1',
    code: 'ART-2026-021',
    title: 'Digital Transformation in Higher Education: Evidence from Central Asian Universities',
    journal: 'Higher Education Policy and Management',
    indexation: 'Scopus / Web of Science',
    quartile: 'Q1',
    stage: 'В печати',
    stageColor: 'bg-emerald-500/10 text-emerald-600',
    coauthors: ['Алтын Бақытжанова', 'Динара Ахметова', 'Ерлан Муратов'],
    lastUpdate: '28.08.2026',
    doi: '10.1016/j.hepm.2026.04.012'
  },
  {
    id: 'a2',
    code: 'ART-2026-031',
    title: 'Econometric Modeling of Regional Economic Disparities: Policy Implications for Kazakhstan',
    journal: 'Journal of Applied Economic Sciences',
    indexation: 'Scopus',
    quartile: 'Q2',
    stage: 'Рецензирование',
    stageColor: 'bg-blue-500/10 text-blue-600',
    coauthors: ['Алтын Бақытжанова', 'Бауржан Касымов', 'Асет Сулейменов'],
    lastUpdate: '20.08.2026'
  },
  {
    id: 'a3',
    code: 'PUB-2025-014',
    title: 'Green Growth and ESG Disclosures in Emerging Markets: The Case of Central Asia',
    journal: 'Environmental and Sustainability Indicators',
    indexation: 'Scopus / Web of Science',
    quartile: 'Q1',
    stage: 'Опубликовано',
    stageColor: 'bg-emerald-500/10 text-emerald-600',
    coauthors: ['Алтын Бақытжанова', 'Нурлан Оспанов'],
    lastUpdate: '15.06.2026',
    doi: '10.1016/j.indic.2025.100281'
  },
  {
    id: 'a4',
    code: 'COA-2026-009',
    title: 'Assessing Academic Integrity and Publication Ethics in Post-Soviet Research Environments',
    journal: 'Accountability in Research',
    indexation: 'Scopus',
    quartile: 'Q2',
    stage: 'Доработка по замечаниям',
    stageColor: 'bg-amber-500/10 text-amber-600',
    coauthors: ['Алтын Бақытжанова', 'Айгерим Жумагулова', 'Гульнар Бектурганова'],
    lastUpdate: '02.09.2026'
  },
  {
    id: 'a5',
    code: 'ART-2026-044',
    title: 'Comparative Analysis of Research Grant Funding Mechanisms: Best Practices and Challenges',
    journal: 'Science and Public Policy',
    indexation: 'Scopus',
    quartile: 'Q2',
    stage: 'Первичная вычитка',
    stageColor: 'bg-violet-500/10 text-violet-600',
    coauthors: ['Алтын Бақытжанова', 'Зауреш Ибраева'],
    lastUpdate: '04.09.2026'
  }
];

export const PartnerSharedArticlesView: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState<'all' | 'published' | 'review' | 'revision'>('all');
  const [notice, setNotice] = useState<string | null>(null);

  const filtered = PRESET_ARTICLES.filter(a => {
    const matchSearch =
      !searchQuery ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.journal.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.coauthors.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchSearch) return false;
    if (filterStage === 'published') return a.stage === 'Опубликовано' || a.stage === 'В печати';
    if (filterStage === 'review') return a.stage === 'Рецензирование';
    if (filterStage === 'revision') return a.stage === 'Доработка по замечаниям';
    return true;
  });

  const handleAction = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border/40 pb-4">
        <div>
          <button
            onClick={() => navigate('/app/partner')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeftIcon size={14} />
            В кабинет научного партнёра
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center font-bold">
              <BookOpenIcon size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Совместные статьи и публикации
              </h1>
              <p className="text-xs text-muted-foreground">
                Реестр коллективных научных рукописей, квартили журналов, стадии рецензирования и DOI
              </p>
            </div>
          </div>
        </div>
      </div>

      {notice && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2Icon size={16} className="flex-shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Всего статей</span>
            <BookOpenIcon size={16} className="text-violet-500" />
          </div>
          <div className="text-2xl font-black text-foreground">8</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">В текущем академическом цикле</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Scopus Q1 / Q2</span>
            <AwardIcon size={16} className="text-amber-500" />
          </div>
          <div className="text-2xl font-black text-foreground">5</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Высокий импакт-фактор</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">На рецензии</span>
            <ClockIcon size={16} className="text-blue-500" />
          </div>
          <div className="text-2xl font-black text-foreground">2</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Ожидается ответ редакции</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Опубликовано</span>
            <CheckCircle2Icon size={16} className="text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-foreground">4</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Проиндексировано в Scopus</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border/40 pb-3">
        <div className="flex items-center gap-1 overflow-x-auto text-xs">
          {(
            [
              { id: 'all', label: 'Все статьи' },
              { id: 'published', label: 'Опубликованные' },
              { id: 'review', label: 'На рецензировании' },
              { id: 'revision', label: 'На доработке' }
            ] as const
          ).map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilterStage(tab.id)}
              className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filterStage === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Поиск по названию, журналу или автору..."
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(art => (
          <div
            key={art.id}
            className="p-4 rounded-xl border border-border/60 bg-card hover:border-primary/40 hover:shadow-xs transition-all space-y-3"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-muted text-muted-foreground">
                    {art.code}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-600">
                    {art.quartile}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {art.indexation}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-foreground leading-snug">
                  {art.title}
                </h3>
                <p className="text-xs text-muted-foreground">
                  Журнал: <strong className="text-foreground font-medium">{art.journal}</strong>
                </p>
              </div>

              <div className="flex sm:flex-col items-end gap-1.5 flex-shrink-0">
                <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${art.stageColor}`}>
                  {art.stage}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Обновлено: {art.lastUpdate}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/30 text-xs">
              <div className="flex items-center gap-1.5 text-muted-foreground mr-auto">
                <UsersIcon size={14} className="text-muted-foreground/70" />
                <span className="text-[11px]">Соавторы:</span>
                <span className="text-foreground font-medium text-[11px]">
                  {art.coauthors.join(', ')}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {art.doi && (
                  <button
                    onClick={() => handleAction(`Переход по DOI: https://doi.org/${art.doi}`)}
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                  >
                    <ExternalLinkIcon size={14} />
                    DOI: {art.doi}
                  </button>
                )}
                <button
                  onClick={() => handleAction(`Черновик ${art.code} открыт для редактирования`)}
                  className="px-2.5 py-1 rounded-lg border border-border/60 hover:bg-muted text-foreground text-[11px] font-medium transition-colors"
                >
                  Черновик статьи
                </button>
                <button
                  onClick={() => handleAction(`Сертификат соавторства ${art.code} загружен`)}
                  className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-[11px] font-medium transition-colors"
                >
                  Сертификат
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
